import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Loader from "./components/Loader";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import PaymentResult from "./components/PaymentResult";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure";
import FacebookPixel from "./components/FacebookPixel";
import GoogleAnalytics from "./components/GoogleAnalytics";

import AOS from "aos";
import "aos/dist/aos.css";

// Lazy-loaded pages
const HomePage = lazy(() => import("./pages/HomePage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const JoinProviderPage = lazy(() => import("./pages/JoinProviderPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const BookPage = lazy(() => import("./pages/BookPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogDetailsPage = lazy(() => import("./pages/BlogDetailsPage"));

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const ProviderCompleteProfile = lazy(() =>
  import("./pages/ProviderCompleteProfile")
);
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const Payment = lazy(() => import("./pages/Payment"));
const IncomingJobRequests = lazy(() => import("./pages/IncomingJobRequests"));
const AcceptRejectJobs = lazy(() => import("./pages/AcceptRejectJobs"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const BankDetailsPage = lazy(() => import("./pages/BankDetailsPage"));
const JobCalendar = lazy(() => import("./pages/JobCalendar"));
const BookingHistory = lazy(() => import("./pages/BookingHistory"));
const EarningsSummary = lazy(() => import("./pages/EarningsSummary"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const RefundCancellationPolicy = lazy(() =>
  import("./pages/RefundCancellationPolicy")
);

// Protected route component
function PrivateRoute({ children }) {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

const useDisableRightClickAndF5 = () => {
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // Disable F5 key
    const handleKeyDown = (e) => {
      if ((e.which || e.keyCode) === 116) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
};

function AppRoutes() {
  useDisableRightClickAndF5();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-in-out",
    });
  }, []);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const user = JSON.parse(localStorage.getItem("user"));

    if (isLoggedIn && location.pathname === "/login") {
      const base = process.env.PUBLIC_URL || "";
      if (user && user.tc_tu_user_type === "customer") {
        // navigate("/user/dashboard", { replace: true });
        window.location.href = `${base}/user/dashboard`;
      } else if (user && user.tc_tu_user_type === "provider") {
        window.location.href = `${base}/provider/dashboard`;
        // navigate("/provider/dashboard", { replace: true });
      } else {
        window.location.href = `${base}`;
        // navigate("/", { replace: true });
      }
    }
  }, [location.pathname]);

  return (
    <>
      <Header />
      <main className="flex-grow-1">
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/join" element={<JoinProviderPage />} />
            <Route path="/faqs" element={<FaqPage />} />
            <Route path="/blogs" element={<BlogListPage />} />
            <Route path="/blogs/:id" element={<BlogDetailsPage />} />
            <Route path="/services" element={<ServicePage />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/payment-result" element={<PaymentResult />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failure" element={<PaymentFailure />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsAndConditions />} />
            <Route
              path="/refund-cancellation-policy"
              element={<RefundCancellationPolicy />}
            />
            <Route
              path="/services/:serviceName"
              element={<ServiceDetailPage />}
            />
            <Route
              path="/service/:serviceName"
              element={<ServiceDetailPage />}
            />
            <Route path="/book/:serviceName" element={<BookPage />} />

            {/* Protected User Dashboard */}
            <Route
              path="/provider/jobrequest"
              element={
                <PrivateRoute>
                  <IncomingJobRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/acceptrejectjobs"
              element={
                <PrivateRoute>
                  <AcceptRejectJobs />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/editprofile"
              element={
                <PrivateRoute>
                  <EditProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/bankdetails"
              element={
                <PrivateRoute>
                  <BankDetailsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/jobcalender"
              element={
                <PrivateRoute>
                  <JobCalendar />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/bookhistory"
              element={
                <PrivateRoute>
                  <BookingHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/earningsummary"
              element={
                <PrivateRoute>
                  <EarningsSummary />
                </PrivateRoute>
              }
            />

            <Route
              path="/user/dashboard"
              element={
                <PrivateRoute>
                  <UserDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/dashboard"
              element={
                <PrivateRoute>
                  <ProviderDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/provider/complete-profile"
              element={
                <PrivateRoute>
                  <ProviderCompleteProfile />
                </PrivateRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}
function App() {
  return (
    // <Router
    //   future={{
    //     v7_startTransition: true,
    //     v7_relativeSplatPath: true,
    //     v7_fetcherPersist: true,
    //     v7_normalizeFormMethod: true,
    //     v7_prependBasename: true,
    //   }}
    // >
    <Router basename="/">
      <GoogleAnalytics measurementId="G-KMVWZ0ES19" />
      <FacebookPixel pixelId="1554411175970093" />
      <ScrollToTop />
      <AppRoutes />
      <ToastContainer position="top-right" autoClose={3000} />
    </Router>
  );
}

export default App;
